// src/services/sssContributionTable.js

/**
 * Client-side reader + resolver for the GLOBAL `contributionTables/sss`
 * collection published by the platform app (BL-062). This is the sahod-app
 * consumer (BL-063): it powers the salary-derived SSS suggestion chip at
 * employee setup.
 *
 * The engine never does a live bracket lookup — payroll reads the frozen
 * `employment.sssContribution`. The bracket only matters at ONE moment:
 * employee setup, deciding what to suggest for that saved figure. So this
 * service is read-only and suggestion-only; it never writes.
 *
 * Contract (mirrors sahod-platform PRD_SSSContributionTable "Resolution"):
 *  - Version docs live at `contributionTables/sss/versions/{effectiveDate}`;
 *    the doc id IS the 'YYYY-MM-DD' effective date.
 *  - In-force version = the greatest effectiveDate ≤ today (Asia/Manila).
 *  - Employee SSS share = `eeRegular + eeWisp` (EC is employer-only; totals are
 *    derived, never stored) — a lookup over the version's own `rows`, never a
 *    re-implementation of the circular's math.
 *  - Fallback: when no version resolves (empty collection / before the first
 *    publish), the caller falls back to manual entry (no suggestion).
 *
 * SSS brackets on MONTHLY compensation. Monthly-salary employees bracket on
 * `monthlySalary` directly; daily-salary employees have no stored monthly
 * figure, so we derive one from the company's own monthly→daily rate factor
 * (`settings/payrollRates.dailyRateFactor`): the inverse of
 * `dailyRate = monthlySalary × 12 ÷ factor` is `monthly = daily × factor ÷ 12`.
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  round2,
  resolveInForceVersion,
  monthlyGrossForEmployee,
} from './contributionTableClient';

// Version resolution + the monthly-salary basis are shared with the PhilHealth
// consumer (BL-073) via contributionTableClient.js — one definition. Re-exported
// here so existing importers (this service's test, StepEmployment) keep their
// `from './sssContributionTable'` imports (public surface byte-unchanged).
export { resolveInForceVersion, monthlyGrossForEmployee };

/**
 * Read every published SSS contribution-table version. Cardinality is a handful
 * per decade, so read them all and resolve in memory. Each returned object is
 * the stored version doc with `effectiveDate` taken from the doc id.
 *
 * @returns {Promise<Array<Object>>} version docs (may be empty)
 */
export async function fetchSssTableVersions() {
  const snap = await getDocs(
    collection(db, 'contributionTables', 'sss', 'versions')
  );
  return snap.docs.map((d) => ({ effectiveDate: d.id, ...d.data() }));
}

/**
 * Look up the employee SSS share for a monthly compensation. Bounds are
 * inclusive; the last band is open-ended (`compMax === null`). Returns
 * `round2(eeRegular + eeWisp)`, or null when the version/rows are missing or
 * the gross is not a usable number.
 *
 * @param {?Object} version - an in-force version doc
 * @param {number} monthlyGross - monthly compensation in pesos
 * @returns {?number} the employee SSS contribution, or null
 */
export function lookupEmployeeSssShare(version, monthlyGross) {
  if (!version || !Array.isArray(version.rows)) return null;
  if (typeof monthlyGross !== 'number' || !isFinite(monthlyGross) ||
      monthlyGross < 0) {
    return null;
  }
  const row = version.rows.find(
    (r) =>
      monthlyGross >= r.compMin &&
      (r.compMax === null || r.compMax === undefined ||
        monthlyGross <= r.compMax)
  );
  if (!row) return null;
  return round2((row.eeRegular || 0) + (row.eeWisp || 0));
}

/**
 * The suggested SSS contribution for an employee against an in-force version.
 * Composes monthlyGrossForEmployee + lookupEmployeeSssShare. Returns null
 * (→ no suggestion, manual entry) whenever the salary can't be resolved to a
 * monthly gross or the gross falls outside the table.
 *
 * @param {Object} args
 * @param {?Object} args.version - in-force version doc (null → null)
 * @param {string} args.salaryType
 * @param {number|string} [args.monthlySalary]
 * @param {number|string} [args.dailySalary]
 * @param {number|string} [args.dailyRateFactor]
 * @returns {?number} suggested employee SSS contribution, or null
 */
export function suggestEmployeeSss({
  version,
  salaryType,
  monthlySalary,
  dailySalary,
  dailyRateFactor,
}) {
  const gross = monthlyGrossForEmployee({
    salaryType,
    monthlySalary,
    dailySalary,
    dailyRateFactor,
  });
  if (gross === null) return null;
  return lookupEmployeeSssShare(version, gross);
}
